import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

interface TranslationCache {
  [key: string]: {
    [lang: string]: string;
  };
}

// Cache em localStorage para traduções automáticas
const CACHE_KEY = 'auto_translations_cache';

// Serviço de tradução automática
class AutoTranslationService {
  private cache: TranslationCache = {};

  constructor() {
    this.loadCache();
  }

  private loadCache() {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) this.cache = JSON.parse(cached);
    } catch (error) {
      console.warn('Erro ao carregar cache de traduções:', error);
    }
  }

  private saveCache() {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(this.cache));
    } catch (error) {
      console.warn('Erro ao salvar cache de traduções:', error);
    }
  }

  // Traduções manuais para casos importantes
  private manualTranslations: TranslationCache = {
    "Quem é Savana": {
      en: "About Savana",
      de: "Über Savana",
      es: "Sobre Savana",
      fr: "À propos de Savana"
    },
    "Meu Perfil": {
      en: "My Profile",
      de: "Mein Profil",
      es: "Mi Perfil",
      fr: "Mon Profil"
    },
    "Sair": {
      en: "Logout",
      de: "Abmelden",
      es: "Cerrar sesión",
      fr: "Se déconnecter"
    },
    "Início": {
      en: "Home",
      de: "Startseite",
      es: "Inicio",
      fr: "Accueil"
    },
    "Produtos": {
      en: "Products",
      de: "Produkte",
      es: "Productos",
      fr: "Produits"
    },
    "Carrinho": {
      en: "Cart",
      de: "Warenkorb",
      es: "Carrito",
      fr: "Panier"
    },
    "Contato": {
      en: "Contact",
      de: "Kontakt",
      es: "Contacto",
      fr: "Contact"
    },
    "Entrar": {
      en: "Login",
      de: "Anmelden",
      es: "Iniciar sesión",
      fr: "Se connecter"
    },
    "Comprar Agora": {
      en: "Buy Now",
      de: "Jetzt kaufen",
      es: "Comprar Ahora",
      fr: "Acheter maintenant"
    },
    "Ver Produtos": {
      en: "View Products",
      de: "Produkte ansehen",
      es: "Ver Productos",
      fr: "Voir les produits"
    },
    "Adicionar ao Carrinho": {
      en: "Add to Cart",
      de: "In den Warenkorb",
      es: "Añadir al Carrito",
      fr: "Ajouter au panier"
    }
  };

  getTranslation(text: string, targetLang: string): string {
    // 1. Se é o idioma padrão (pt), retornar o texto original
    if (targetLang === 'pt') {
      return text;
    }

    // 2. Verificar traduções manuais primeiro
    if (this.manualTranslations[text] && this.manualTranslations[text][targetLang]) {
      return this.manualTranslations[text][targetLang];
    }

    // 3. Verificar cache
    if (this.cache[text] && this.cache[text][targetLang]) {
      return this.cache[text][targetLang];
    }

    // 4. Por enquanto, retornar o texto original (depois implementaremos API)
    return text;
  }
}

const translationService = new AutoTranslationService();

// Hook principal para tradução automática
export const useAutoTranslation = () => {
  const { t, i18n } = useTranslation();

  // Função para traduzir automaticamente textos
  const at = (text: string): string => {
    return translationService.getTranslation(text, i18n.language);
  };

  return {
    at,
    currentLanguage: i18n.language,
    changeLanguage: i18n.changeLanguage,
    t // Manter acesso ao t original
  };
};
